<template>
  <b-modal v-model="inputVal" modal-class="stocks-modal" size="xl" centered hide-footer
  @hidden="resetstockIdsArr">
    <template #modal-title>兌換情況</template>
    <template #default>
      <CouponStockCard :id="id" :isShowStockQuantity="isShowStockQuantity"></CouponStockCard>
      <div class="export-wrapper">
        <BaseButton v-if="!isPayment"
                    v-stream:click="exportData$">
          匯出
        </BaseButton>
      </div>
      <DataTable
        v-if="list"
        :fields="fields"
        :mobileFields="mobileFields"
        :items="list"
        :total-result="total"
        :options.sync="options"
        :loading="loading"
        small
        showRowDetails
        v-stream:mobile-arrow-click="onArrowClick$"
        v-stream:export-click="exportData$"
      >
        <template v-slot:cell(state)="{ item }">
          <StockStatusLabel :value="item.state"></StockStatusLabel>
        </template>
        <template v-slot:cell(isUsed)="{ item }">
          <StockUsedLabel :value="item.isUsed"></StockUsedLabel>
        </template>

        <template v-slot:cell(answers)="{ item, toggleDetails, detailsShowing }">
          <img v-if="item.state === 'FINISHED'"
            src="../../assets/img/icon/arrow-down.svg"
            alt="arrow down icon"
            style="cursor: pointer;"
            :style="{ transform: detailsShowing ? 'rotate(-180deg)' : '' }"
            v-stream:click="{ subject: onArrowClick$, data: item }"
            @click="toggleDetails"
          />
        </template>
        <template v-slot:row-details-custom="{ item }">
          <CustomQuestionTable :QnA="item.answers"></CustomQuestionTable>
        </template>
        <!-- mobile only -->
        <template v-slot:isUsed="{ item }">
          <StockUsedLabel :value="item.isUsed"></StockUsedLabel>
        </template>
        <!-- end of mobile only -->
      </DataTable>
      <b-skeleton-table v-else :rows="5" :columns="fields.length"></b-skeleton-table>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { merge } from 'rxjs';
import {
  debounceTime, filter, pluck, switchMap, tap, map,
} from 'rxjs/operators';
import {
  BModal,
  BSkeletonTable,
} from 'bootstrap-vue';
import BaseButton from '@/components/BaseButton.vue';
import http from '@/helpers/http';
import exportMixin from '../../mixins/export';
import vModelMixin from '../../mixins/v-model';
import dataTableMixin from '../../mixins/data-table';
import CouponStockCard from './CouponStockCard.vue';
import DataTable from '../DataTable.vue';
import StockStatusLabel from './StockStatusLabel.vue';
import StockUsedLabel from './StockUsedLabel.vue';
import CustomQuestionTable from './CustomQuestionTable.vue';

const ADMIN_HEADERS = [
  { key: 'user.name', label: '會員名稱' },
  { key: 'user.id', label: '會員編號' },
  { key: 'state', label: '狀態' },
  { key: 'updatedAt', label: '兌換時間' },
  { key: 'code', label: '優惠碼' },
  { key: 'isUsed', label: '使用狀況' },
  { key: 'time', label: '使用時間' },
];
const MERCHANT_HEADERS = [
  { key: 'code', label: '優惠碼' },
  { key: 'state', label: '狀態' },
  { key: 'isUsed', label: '使用狀況' },
];

export default {
  mixins: [vModelMixin, dataTableMixin, exportMixin],
  components: {
    BModal,
    BSkeletonTable,
    CouponStockCard,
    DataTable,
    StockStatusLabel,
    StockUsedLabel,
    CustomQuestionTable,
    BaseButton,
  },
  props: {
    id: Number,
    isShowStockQuantity: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    stockIds: [],
  }),
  computed: {
    isPayment() {
      return this.$route.name === 'EditPaymentCoupon';
    },
    ...mapGetters(['role']),
    fields() {
      let adminHeaders = ADMIN_HEADERS;
      let merchantHeaders = MERCHANT_HEADERS;
      if (this.role === 'admin') {
        if (this.isPayment) {
          adminHeaders = [
            ...ADMIN_HEADERS,
            { key: 'ref', label: '訂單編號' },
            { key: 'answers', label: '查看預設問題', hideRowInMobile: true },
          ];
        } else {
          adminHeaders = [
            ...ADMIN_HEADERS,
            { key: 'answers', label: '查看預設問題', hideRowInMobile: true },
          ];
        }
        return adminHeaders;
      }
      if (this.isPayment) {
        merchantHeaders = [
          ...MERCHANT_HEADERS,
          { key: 'ref', label: '訂單編號' },
          { key: 'answers', label: '查看預設問題', hideRowInMobile: true },
        ];
      } else {
        merchantHeaders = [
          ...MERCHANT_HEADERS,
          { key: 'answers', label: '查看預設問題', hideRowInMobile: true },
        ];
      }
      return merchantHeaders;
    },
    mobileFields() {
      if (this.role === 'admin') {
        return [
          { key: 'user.name', label: '會員名稱' },
          { key: 'user.id', label: '會員編號' },
          { key: 'state', label: '狀態' },
        ];
      }
      return [];
    },
  },
  methods: {
    isStockStillHolding(holdUntil) {
      if (!holdUntil) return false;

      const diff = +new Date() - Date.parse(holdUntil.toString());
      return diff <= 0;
    },
    resetstockIdsArr() {
      this.stockIds = [];
    },
  },
  domStreams: ['onArrowClick$', 'exportData$'],
  subscriptions() {
    const getData$ = () => this.httpRequest$({
      method: 'GET',
      url: `/agc-${this.role}-api/coupons/stocks/${this.id}`,
    });

    const onModalShow$ = this.$watchAsObservable('value').pipe(
      pluck('newValue'),
      filter((val) => val),
    );

    const getQnA$ = (data) => http({
      method: 'GET',
      url: `/agc-${this.role}-api/coupons/CustomQuestions/${data.stockId}`,
    });

    return {
      list: merge(this.onOptionsUpdate$, onModalShow$).pipe(
        tap(() => {
          this.loading = true;
        }),
        debounceTime(100),
        switchMap(getData$),
        map((stocks) => stocks.map((stock) => {
          if (stock.state === 'HOLDING' && !this.isStockStillHolding(stock.holdUntil)) {
            stock.state = 'AVAILABLE';
            stock.user = {};
          }
          return stock;
        })),
      ),
      qNA: this.onArrowClick$.pipe(
        map((data) => {
          if (data.event.msg?.item) {
            // handle mobile view arrow btn
            return data.event.msg.item;
          }
          // handle desktop view arrow btn
          return data.data;
        }),
        filter((data) => {
          for (let i = 0; i < this.stockIds.length; i += 1) {
            if (this.stockIds[i] === data.stockId) {
              return false;
            }
          }
          return true;
        }),
        switchMap(getQnA$),
        tap((data) => {
          const newlist = [...this.list];
          this.list.forEach((item, index) => {
            if (item.stockId === data.stockId) {
              if (data.result) {
                // avoid json parse null value in dollar_redemption_items.answers
                newlist[index].answers = JSON.parse(data.result.answers.length > 0
                  ? data.result.answers : null);
                this.list = newlist;
              }
              this.stockIds.push(data.stockId);
            }
          });
        }),
      ),
      exportData: this.exportData$.pipe(
        switchMap(() => axios({
          method: 'GET',
          url: `/agc-${this.role}-api/export/stocks`,
          params: {
            ...this.filter,
            q: this.keyword,
            id: this.id,
          },
          responseType: 'blob',
        }).then((res) => {
          this.downloadFile(
            res.data,
            this.generateFileName(
              `${this.$route.meta.paymentType === 'UFUN' ? '' : 'paid-'}stock-list-data`,
            ),
          );
        })),
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/data-table/status-table-content";
@import "@/assets/scss/modal/status-modal";
.export-wrapper {
  width:100%;
  text-align: right;
  padding: 0 25px;

  button {
    display: inline-block;
  }
}
</style>
